// scenarioRailItems.ts
// The Scenarios suite's routes and its in-page rail items. Pure (no React,
// no icons) so `node --test` can import it; ScenariosRail.tsx maps each id to
// an icon. Adding a scenario (partial or timed closures) is one entry here,
// one icon in ScenariosRail.tsx and one route in src/routes/routes.tsx.

export const SCENARIOS_BASE_PATH = "/digitaltwin/scenarios";

export type ScenarioRailId = "closure" | "zone30" | "jobs" | "analysis" | "drafts" | "settings";

export interface ScenarioRailItem {
  id: ScenarioRailId;
  /** Tooltip and aria-label. */
  label: string;
  /** Relative to SCENARIOS_BASE_PATH. */
  path: string;
  /** Shown greyed out, not clickable, with `disabledHint` as the tooltip. */
  disabled?: boolean;
  disabledHint?: string;
  /** This item shows the active-job count badge. */
  showsActiveJobs?: boolean;
}

export const SCENARIO_RAIL_ITEMS: readonly ScenarioRailItem[] = [
  { id: "closure", label: "Road closure", path: "closure" },
  // Any speed, up or down; the id and path keep the original "zone30".
  { id: "zone30", label: "Speed limit", path: "zone30" },
  { id: "jobs", label: "Jobs", path: "jobs", showsActiveJobs: true },
  { id: "analysis", label: "Analysis", path: "analysis" },
  { id: "drafts", label: "Drafts", path: "drafts" },
  { id: "settings", label: "Settings", path: "settings" },
];

export const scenarioPath = (path: string): string => `${SCENARIOS_BASE_PATH}/${path}`;

/** A job's result on the Analysis page (the old `jobs/:jobId` redirects here). */
export const analysisPath = (jobId: string): string =>
  scenarioPath(`analysis/${encodeURIComponent(jobId)}`);

/**
 * The rail item a pathname belongs to. `analysis/:jobId` (and the old
 * `jobs/:jobId`, which redirects there) highlights "Analysis"; the bare
 * `jobs` list highlights "Jobs".
 */
export const activeRailItem = (pathname: string): ScenarioRailId | null => {
  const prefix = `${SCENARIOS_BASE_PATH}/`;
  if (!pathname.startsWith(prefix)) return null;
  const [first, second] = pathname.slice(prefix.length).split("/").filter(Boolean);
  if (first === "jobs") return second ? "analysis" : "jobs";
  const item = SCENARIO_RAIL_ITEMS.find((candidate) => candidate.path === first);
  return item && !item.disabled ? item.id : null;
};
